import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { teacherGetRoster, teacherMarkAttendance } from "@/lib/rewards";

/** Used inside live class page or sessions list. */
export default function AttendancePanel({ sessionId, onMarked }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () =>
    teacherGetRoster(sessionId)
      .then((d) => {
        setData(d);
        const pre = new Set(
          d.roster.filter((r) => r.attended).map((r) => r.student_id)
        );
        setPicked(pre);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Roster failed"));

  useEffect(() => {
    if (sessionId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const submit = async () => {
    setBusy(true);
    try {
      const newOnes = Array.from(picked).filter(
        (sid) => !data.roster.find((r) => r.student_id === sid)?.attended
      );
      if (newOnes.length === 0) {
        toast.message("No new attendees selected");
        return;
      }
      const r = await teacherMarkAttendance(sessionId, newOnes);
      toast.success(`Marked ${r.marked} student(s) present 🎉`);
      onMarked?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <Card className="border-stone-200" data-testid="attendance-panel">
      <CardHeader>
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-amber-600" /> Mark attendance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.roster.length === 0 ? (
          <p className="text-sm text-stone-500">No students enrolled yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.roster.map((r) => (
              <li
                key={r.student_id}
                className="flex items-center justify-between border border-stone-200 rounded px-2 py-1"
                data-testid={`roster-${r.student_id}`}
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={picked.has(r.student_id) || r.attended}
                    disabled={r.attended}
                    onCheckedChange={(v) => {
                      setPicked((prev) => {
                        const n = new Set(prev);
                        if (v) n.add(r.student_id);
                        else n.delete(r.student_id);
                        return n;
                      });
                    }}
                    data-testid={`roster-checkbox-${r.student_id}`}
                  />
                  <span>{r.name}</span>
                </label>
                <span className="text-xs text-stone-500">
                  {r.attended ? "Marked ✓" : r.payment_status}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Button
          onClick={submit}
          disabled={busy}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="submit-attendance-button"
        >
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Mark attendance
        </Button>
      </CardContent>
    </Card>
  );
}
